import React, { useMemo } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/pro-regular-svg-icons";

import Checkbox from "components/form/checkbox/Checkbox";
import DataTable from "components/dataTable/DataTable";
import MessageDisplay from "components/messageDisplay/MessageDisplay";
import Sheet from "components/sheet/Sheet";
import Portal from "elements/Portal";
import { LocaleCurrency } from "helpers/Locale";

import { TFutureBookingPricingParticipant, TFutureBookingPricingSection } from "./types";

import "./updateFutureBookingPricingModal.scss";

export interface IFutureBookingPricingModalProps {
  open: boolean;
  sections: TFutureBookingPricingSection[];
  applying: boolean;
  onClose: () => void;
  onApply: () => void;
  onToggleParticipant: (sectionId: TFutureBookingPricingSection["id"], participantId: number, checked: boolean) => void;
  onToggleSelectAll: (sectionId: TFutureBookingPricingSection["id"], checked: boolean) => void;
}

function formatTeeTime(participant: TFutureBookingPricingParticipant) {
  return moment(`${participant.tee_time_date} ${participant.tee_time_start_time}`, "YYYY-MM-DD HH:mm:ss").format(
    "MMM D, YYYY h:mm A",
  );
}

type TFutureBookingPricingSectionTableProps = {
  section: TFutureBookingPricingSection;
  formatFee: (title: string | null, price: number | null) => React.ReactNode;
  onToggleParticipant: IFutureBookingPricingModalProps["onToggleParticipant"];
  onToggleSelectAll: IFutureBookingPricingModalProps["onToggleSelectAll"];
};

function FutureBookingPricingSectionTable(props: TFutureBookingPricingSectionTableProps) {
  const { section, formatFee, onToggleParticipant, onToggleSelectAll } = props;
  const { t } = useTranslation();

  const eligibleParticipants = useMemo(
    () => section.participants.filter(participant => participant.eligible),
    [section.participants],
  );

  const allEligibleSelected =
    eligibleParticipants.length > 0 &&
    eligibleParticipants.every(participant => section.selectedParticipantIds.includes(participant.participant_id));

  if (section.participants.length === 0) {
    return (
      <MessageDisplay
        title={t("No upcoming bookings")}
        content={t("This customer has no upcoming bookings at this facility.")}
        icon="magnifying-glass"
      />
    );
  }

  return (
    <DataTable
      textWrap
      columns={[
        {
          label: "",
          width: "3rem",
          content: (
            <Checkbox
              size="medium"
              labelHidden
              checked={allEligibleSelected}
              onChange={event => onToggleSelectAll(section.id, event.target.checked)}
            />
          ),
        },
        { label: t("Booking") },
        { label: section.feeColumnLabel },
        { label: t("Status"), width: "9rem" },
      ]}
    >
      {section.participants.map(participant => {
        const isSelected = section.selectedParticipantIds.includes(participant.participant_id);

        return (
          <tr key={`${section.id}-${participant.participant_id}`}>
            <td>
              <Checkbox
                size="medium"
                labelHidden
                checked={isSelected}
                disabled={!participant.eligible}
                onChange={event => onToggleParticipant(section.id, participant.participant_id, event.target.checked)}
              />
            </td>
            <td>
              <div className="future-booking-pricing-modal__booking">
                <span>{formatTeeTime(participant)}</span>
                {participant.course_title ? (
                  <span className="future-booking-pricing-modal__booking-course">{participant.course_title}</span>
                ) : null}
              </div>
            </td>
            <td>
              <div className="future-booking-pricing-modal__fee-change">
                <span className="future-booking-pricing-modal__fee-current">
                  {formatFee(participant.current_variant_title, participant.current_price)}
                </span>
                {participant.eligible ? (
                  <>
                    <FontAwesomeIcon icon={faArrowRight} />
                    {formatFee(participant.proposed_variant_title, participant.proposed_price)}
                  </>
                ) : null}
              </div>
            </td>
            <td>
              {participant.eligible ? (
                t("Ready")
              ) : (
                <span className="future-booking-pricing-modal__skip-reason">{participant.skip_reason}</span>
              )}
            </td>
          </tr>
        );
      })}
    </DataTable>
  );
}

export default function FutureBookingPricingModal(props: IFutureBookingPricingModalProps) {
  const { open, sections, applying, onClose, onApply, onToggleParticipant, onToggleSelectAll } = props;
  const { t } = useTranslation();

  const hasSelection = sections.some(section => section.selectedParticipantIds.length > 0);

  function handleClose() {
    if (applying) {
      return;
    }

    onClose();
  }

  function formatFee(title: string | null, price: number | null) {
    if (!title && price === null) {
      return t("None");
    }

    if (price === null) {
      return title ?? t("None");
    }

    return (
      <>
        {title ? <span>{title} — </span> : null}
        <LocaleCurrency amount={price} />
      </>
    );
  }

  return (
    <Portal isMounted={open}>
      <Sheet
        open={open}
        title={t("Future booking update")}
        supportText={t("Customer information has changed, update future bookings?")}
        size="medium"
        height="flexible"
        onCancel={handleClose}
        onOk={() => void onApply()}
        closable
        okText={t("Update selected")}
        cancelText={t("Not now")}
        okDisabled={!hasSelection}
        okLoading={applying}
        stacked
      >
        <div className="future-booking-pricing-modal">
          {sections.map(section => (
            <div key={section.id} className="future-booking-pricing-modal__section">
              {sections.length > 1 ? (
                <h3 className="future-booking-pricing-modal__section-title">{section.title}</h3>
              ) : null}
              <FutureBookingPricingSectionTable
                section={section}
                formatFee={formatFee}
                onToggleParticipant={onToggleParticipant}
                onToggleSelectAll={onToggleSelectAll}
              />
            </div>
          ))}
        </div>
      </Sheet>
    </Portal>
  );
}
